/**
 * ServiceGrid — Zone-2 of the SUPER AWENI home page.
 *
 * Renders the 12 service "doors" from `doors.config.json` as a 4-column
 * grid (collapses to 3 on the smallest viewports). Each door is a NATIVE
 * <a href="#/route"> tag — never a JS-handler <div> — so:
 *   - INP stays ≤150ms (Google's "good" budget; we target <100ms)
 *   - Browser ctrl/cmd-click + middle-click + right-click open-in-new-tab
 *     all work natively
 *   - Screen readers + keyboard nav work without ARIA hacks
 *
 * Icons are inline SVGs from `icons.ts` (no external icon library = ~0
 * runtime cost). Color tints come from doors.config.json `color` field.
 *
 * Adding/removing/reordering doors: edit `doors.config.json` ONLY. This
 * component re-renders the next mount with the new list.
 */

import type { AweniContext } from '../../types';
import doorsConfig from './doors.config.json';
import { renderIcon } from './icons';

interface DoorEntry {
  id: string;
  labelKey: string;
  icon: string;
  color: string;
  route: string;
  minVersion?: string;
  category?: string;
  /**
   * `primary` doors render in the visible 4×2 grid above-the-fold.
   * `secondary` doors only show inside the More drawer (so the home
   * grid never exceeds the 8-tile cognitive-load threshold per the
   * super-app benchmark — Alipay 9-grid, WeChat 4×2 visible, etc.).
   */
  tier?: 'primary' | 'secondary';
}

interface DoorsConfig {
  _comment?: string;
  _version?: number;
  doors: DoorEntry[];
}

export class ServiceGrid {
  private root: HTMLElement | null = null;
  private ctx: AweniContext;

  constructor(ctx: AweniContext) {
    this.ctx = ctx;
  }

  /** Render synchronously into the given parent element. */
  mount(parent: HTMLElement): void {
    const config = doorsConfig as DoorsConfig;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const root = document.createElement('nav');
    root.className = 'aweni-service-grid';
    root.setAttribute('aria-label', t('home_grid_label') || 'AWENI services');
    this.root = root;

    // Render PRIMARY tier only — secondary tiles live in the More drawer.
    // Doors without `tier` default to primary for backward-compat.
    const visibleDoors = config.doors.filter((d) => d.tier !== 'secondary');
    const items = visibleDoors.map((door) => {
      const label = t(door.labelKey) || door.id;
      // Use href="#/<route>" — hash router catches the change. Native <a>
      // means the browser handles click/touch synchronously: no JS handler
      // required, INP stays low, and screen readers announce as a link.
      const href = `#${door.route.startsWith('/') ? door.route : '/' + door.route}`;
      const colorTint = `${door.color}1A`; // 10% alpha background
      return `
        <a class="aweni-door" href="${href}"
           data-door-id="${door.id}"
           aria-label="${label}">
          <span class="aweni-door-icon-wrap" style="background:${colorTint};color:${door.color};">
            ${renderIcon(door.icon, 28)}
          </span>
          <span class="aweni-door-label">${label}</span>
        </a>`;
    }).join('');

    root.innerHTML = items;
    parent.appendChild(root);
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}
